'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const formatSegment = (segment: string) =>
  segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items = [], className }) => {
  const pathname = usePathname();
  const segments = pathname.split('/').filter(Boolean);

  const generatedItems: BreadcrumbItem[] = segments.map((segment, index) => ({
    label: formatSegment(segment),
    href: `/${segments.slice(0, index + 1).join('/')}`,
  }));

  const providedItems = items.length > 0 ? items : generatedItems;
  const firstLabel = providedItems[0]?.label?.toLowerCase() || '';
  const breadcrumbItems =
    providedItems.length > 0 && firstLabel === 'home'
      ? [...providedItems]
      : [{ label: 'Home', href: '/' }, ...providedItems];

  return (
    <div className={cn('w-full py-3 bg-gray-100 web-only', className)}>
      <nav aria-label="Breadcrumb" className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-gray-500">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            const content = isLast || !item.href ? (
              <span className="font-medium text-gray-900 capitalize">{item.label}</span>
            ) : (
              <Link
                href={item.href}
                className="inline-flex items-center capitalize transition-colors hover:text-primary"
              >
                {index === 0 && <Home className="mr-1.5 h-4 w-4" />}
                <span>{item.label}</span>
              </Link>
            );

            return (
              <React.Fragment key={`${item.label}-${index}`}>
                <li>{content}</li>
                {!isLast && (
                  <li className="text-gray-300" aria-hidden="true">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
